import { useCallback, useEffect, useMemo, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { archiveMonthCounts, archiveYearCounts, completeFollowUp, getTelegramEnabled, listFollowUps, loadProfile, searchArchive, setTelegramEnabled } from "./lib/data";
import type { ArchiveFilters } from "./lib/data";
import { formatDate, initials, relativeDays } from "./lib/format";
import { isConfigured, requireSupabase, supabase } from "./lib/supabase";
import type { ArchiveEntry, FollowUp, PortalView, Profile } from "./types";
import { FollowUpEditor } from "./components/FollowUpEditor";
import { PawTrail, isPawEnabled, setPawEnabled } from "./components/PawTrail";
import { LoginScreen } from "./components/LoginScreen";
import { PeopleView } from "./components/PeopleView";
import { teamMemberName } from "./lib/team";

const nav: Array<{ id: PortalView; label: string; icon: string }> = [
  { id: "dashboard", label: "Overview", icon: "◫" },
  { id: "archive", label: "Archive", icon: "⌕" },
  { id: "followups", label: "Follow-ups", icon: "★" },
  { id: "people", label: "People", icon: "♧" },
];

const currentDate = new Date();
const currentYear = currentDate.getFullYear();
const currentMonth = currentDate.getMonth() + 1;

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [view, setView] = useState<PortalView>("dashboard");
  const [query, setQuery] = useState("");
  // Default to the top-level year browse so the boss lands on clean year folders.
  const [archiveFilters, setArchiveFilters] = useState<ArchiveFilters>({ kind: "all", year: null, month: null, species: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessRequested, setAccessRequested] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [editing, setEditing] = useState<{ entry: ArchiveEntry; followUp?: FollowUp } | null>(null);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => data.subscription.unsubscribe();
  }, []);

  const refresh = useCallback(async () => {
    if (!session) return;
    setError(null);
    try {
      const nextProfile = await loadProfile(session.user.id);
      setProfile(nextProfile);
      if (nextProfile) {
        setFollowUps(await listFollowUps());
      } else {
        setFollowUps([]);
        const { data, error: requestError } = await requireSupabase().rpc("portal_access_request", { p_submit: false });
        if (requestError) throw requestError;
        setAccessRequested(data === true);
      }
    } catch (e) {
      setError(friendlyError(e));
    }
  }, [session]);

  useEffect(() => {
    if (!session) {
      setProfile(null);
      setAccessRequested(false);
      return;
    }
    setLoading(true);
    void refresh().finally(() => setLoading(false));
  }, [refresh, session]);

  const canManage = profile?.role === "admin" || profile?.role === "boss" || profile?.role === "media";
  const overdue = useMemo(
    () => followUps.filter((item) => item.due_at && new Date(item.due_at).getTime() < Date.now()),
    [followUps],
  );
  const unassigned = followUps.filter((item) => !item.assigned_person && !item.assigned_to);

  if (!isConfigured) {
    return (
      <div className="setup-state">
        <div className="brand-mark">R</div>
        <h1>Portal setup required</h1>
        <p>Copy <code>.env.example</code> to <code>.env.local</code> and add the Supabase project URL and publishable key.</p>
      </div>
    );
  }
  if (loading && !session) return <div className="loading-screen"><div className="pulse">R</div></div>;
  if (!session) return <LoginScreen error={error} />;
  if (!loading && !profile) {
    return (
      <main className="approval-shell">
        <section className="approval-card">
          <img className="brand-logo" src="/resq-logo.png" alt="RESQ" />
          <div className="eyebrow">RESQ MEDIA PORTAL</div>
          <h1>{accessRequested ? "Media Portal request received." : "Request Media Portal access"}</h1>
          <p>
            {accessRequested
              ? "Your request was sent to the Media Portal administrator. Check approval after they grant access."
              : "Your Google sign-in worked. Access to RESQ Archive is separate. Request access below only if you also need this Media Portal."}
          </p>
          <p>{session.user.email}</p>
          {error && <div className="alert error">{friendlyError(error)}</div>}
          {!accessRequested && <button className="button primary" disabled={requestingAccess} onClick={() => {
            setRequestingAccess(true);
            setError(null);
            void (async () => {
              try {
                const { data, error: requestError } = await requireSupabase().rpc("portal_access_request", { p_submit: true });
                if (requestError) throw requestError;
                setAccessRequested(data === true);
              } catch (e) { setError(friendlyError(e)); }
              finally { setRequestingAccess(false); }
            })();
          }}>{requestingAccess ? "Sending…" : "Request Media Portal access"}</button>}
          <button className="button secondary" onClick={() => { setLoading(true); void refresh().finally(() => setLoading(false)); }}>
            Check approval
          </button>
          <button className="button primary approval-signout" onClick={() => void requireSupabase().auth.signOut()}>
            Sign out
          </button>
        </section>
      </main>
    );
  }

  return (
    <div className="portal-shell">
      <PawTrail />
      <header className="app-header">
        <div className="portal-brand">
          <img className="brand-logo small" src="/resq-logo.png" alt="RESQ" />
          <span><strong>RESQ <em>Media Portal</em></strong><small>Media Comms</small></span>
          <b className="version-badge">v0.34.0</b>
        </div>
        <nav className="app-nav" aria-label="Portal sections">
          {nav.filter((item) => item.id !== "people" || profile?.role === "admin").map((item) => (
            <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => setView(item.id)}>
              <span>{item.icon}</span>{item.label}
              {item.id === "followups" && followUps.length > 0 && <b>{followUps.length}</b>}
            </button>
          ))}
        </nav>
        <div className="header-status"><span className="live-dot" /><span>Archive ready · 3 drives</span></div>
        {profile && (
          <div className="user-card">
            <span className="avatar">{initials(profile.full_name, profile.email)}</span>
            <span><strong>{profile.full_name || profile.email.split("@")[0]}</strong><small>{profile.role}</small></span>
            <button title="Sign out" onClick={() => void requireSupabase().auth.signOut()}>↗</button>
          </div>
        )}
      </header>

      <main className="workspace">
        <header className="section-header">
          <div><div className="eyebrow">RESQ MEDIA COMMS</div><h1>{nav.find((item) => item.id === view)?.label}</h1></div>
          <div className="topbar-actions"><span className="sync-status">● Synced</span></div>
        </header>

        {error && <div className="alert error">{error}</div>}
        {loading ? <div className="content-loading">Loading archive…</div> : (
          <>
            {view === "people" && profile?.role === "admin" && <PeopleView />}
            {view === "dashboard" && (
              <Dashboard followUps={followUps} overdue={overdue.length} unassigned={unassigned.length} onOpenFollowUps={() => setView("followups")} />
            )}
            {view === "archive" && (
              <ArchiveView query={query} setQuery={setQuery} filters={archiveFilters} setFilters={setArchiveFilters} canManage={Boolean(canManage)} onFollowUp={(entry) => setEditing({ entry })} />
            )}
            {view === "followups" && (
              <FollowUpsView
                items={followUps}
                canManage={Boolean(canManage)}
                onEdit={(item) => item.entry && setEditing({ entry: item.entry, followUp: item })}
                onComplete={async (item) => { await completeFollowUp(item); await refresh(); }}
              />
            )}
          </>
        )}
      </main>

      {editing && profile && (
        <FollowUpEditor
          entry={editing.entry}
          followUp={editing.followUp}
          userId={profile.id}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void refresh(); }}
        />
      )}
    </div>
  );
}

function TelegramToggle() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getTelegramEnabled().then(setEnabled).catch(() => setEnabled(true)); }, []);
  async function flip() {
    if (enabled === null || busy) return;
    const next = !enabled;
    setBusy(true);
    try { await setTelegramEnabled(next); setEnabled(next); } catch { /* keep old state */ } finally { setBusy(false); }
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "flex-end" }}>
      <span className="muted" style={{ fontSize: 13 }}>Telegram notifications</span>
      <button className={`button ${enabled ? "primary" : "secondary"}`} disabled={enabled === null || busy} onClick={() => void flip()} style={{ minWidth: 60 }}>
        {enabled === null ? "…" : enabled ? "On" : "Off"}
      </button>
    </div>
  );
}

function PawToggle() {
  const [on, setOn] = useState(isPawEnabled());
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span className="muted" style={{ fontSize: 13 }}>🐾 Paw-print cursor</span>
      <button className={`button ${on ? "primary" : "secondary"}`} onClick={() => { const n = !on; setPawEnabled(n); setOn(n); }} style={{ minWidth: 60 }}>
        {on ? "On" : "Off"}
      </button>
    </div>
  );
}

function Dashboard({ followUps, overdue, unassigned, onOpenFollowUps }: { followUps: FollowUp[]; overdue: number; unassigned: number; onOpenFollowUps: () => void }) {
  const recent = followUps.slice(0, 5);
  return (
    <div className="page-stack">
      <div style={{ display: "flex", gap: 18, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap" }}>
        <PawToggle />
        <TelegramToggle />
      </div>
      <section className="metrics-grid">
        <Metric label="Active follow-ups" value={followUps.length} note="Across the media team" tone="green" />
        <Metric label="Overdue" value={overdue} note="Needs attention today" tone="orange" />
        <Metric label="Team-wide" value={unassigned} note="Open to all media (no single owner)" tone="blue" />
        <Metric label="Drive sources" value={3} note="2016 through 2026" tone="sand" />
      </section>
      <section className="panel">
        <div className="panel-heading"><div><span className="eyebrow">FOLLOW-UP QUEUE</span><h2>Cases needing attention</h2></div><button className="text-button" onClick={onOpenFollowUps}>View all →</button></div>
        {recent.length === 0 ? <Empty title="No active follow-ups" text="New assignments will appear here." /> : recent.map((item) => <FollowUpRow key={item.id} item={item} />)}
      </section>
    </div>
  );
}

function Metric({ label, value, note, tone }: { label: string; value: number; note: string; tone: string }) {
  return <article className={`metric ${tone}`}><span>{label}</span><strong>{value}</strong><small>{note}</small></article>;
}

function friendlyError(value: unknown): string {
  if (value instanceof Error) return value.message;
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    const candidate = value as { message?: unknown; details?: unknown; hint?: unknown };
    for (const field of [candidate.message, candidate.details, candidate.hint]) {
      if (typeof field === "string" && field.trim()) return field;
    }
  }
  return "The portal could not load your account. Please try again or contact an administrator.";
}

function EntryCard({ entry, canManage, onFollowUp }: { entry: ArchiveEntry; canManage: boolean; onFollowUp: (entry: ArchiveEntry) => void }) {
  return (
    <article className="entry-card">
      <div className="entry-top"><span className={`kind ${entry.kind}`}>{entry.kind === "engagement" ? "folder" : entry.kind}</span></div>
      <div className="entry-copy">
        <h3>{entry.case_id || entry.name}</h3>
        <p className="entry-summary">{entry.case_id && <strong>{entry.name} · </strong>}{[entry.species?.join(", "), entry.location, entry.stage].filter(Boolean).join(" · ") || entry.description || "No additional details"}</p>
        <span className="year">{entry.month ? `${entry.month}.` : ""} {entry.year || "Project"}</span>
      </div>
      <div className="entry-footer">
        <span>{entry.drive_locations?.length || 0} Drive link{entry.drive_locations?.length === 1 ? "" : "s"}</span>
        <div className="entry-actions">
          {entry.drive_locations?.map((loc, i) => (
            <a key={loc.id} className="mini-button" href={loc.web_view_link} target="_blank" rel="noreferrer" title={loc.folder_name}>
              Open Drive{(entry.drive_locations?.length ?? 0) > 1 ? ` ${i + 1}` : ""}
            </a>
          ))}
          {canManage && entry.kind === "case" && <button className="mini-button primary" onClick={() => onFollowUp(entry)}>+ Follow-up</button>}
        </div>
      </div>
    </article>
  );
}

function ArchiveView({ query, setQuery, filters, setFilters, canManage, onFollowUp }: { query: string; setQuery: (value: string) => void; filters: ArchiveFilters; setFilters: (value: ArchiveFilters) => void; canManage: boolean; onFollowUp: (entry: ArchiveEntry) => void }) {
  const years = Array.from({ length: Math.max(11, currentYear - 2016 + 1) }, (_, index) => currentYear - index);
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const [entries, setEntries] = useState<ArchiveEntry[]>([]);
  const [yearTiles, setYearTiles] = useState<{ year: number; count: number }[]>([]);
  const [monthTiles, setMonthTiles] = useState<{ month: number; count: number }[]>([]);

  // Finder-like browse when there's no search text and no type/species filter:
  //  Any year → year folders; a year + Any month → month folders; year + month → that month's folders.
  const noFilters = !query.trim() && filters.kind === "all" && !filters.species;
  const browseRoot = noFilters && !filters.year;
  const browseYear = noFilters && Boolean(filters.year) && !filters.month;

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        if (browseRoot) {
          const [ys, projects] = await Promise.all([archiveYearCounts(), searchArchive("", { ...filters, year: 0, month: null })]);
          if (!cancelled) { setYearTiles(ys); setMonthTiles([]); setEntries(projects); }
        } else if (browseYear) {
          const [ms, loose] = await Promise.all([archiveMonthCounts(filters.year as number), searchArchive("", { ...filters, month: 0 })]);
          if (!cancelled) { setMonthTiles(ms); setYearTiles([]); setEntries(loose); }
        } else {
          const r = await searchArchive(query, filters);
          if (!cancelled) { setYearTiles([]); setMonthTiles([]); setEntries(r); }
        }
      } catch { /* transient */ }
    }, 180);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [query, filters, browseRoot, browseYear]);

  const species = Array.from(new Set(entries.flatMap((entry) => entry.species ?? []).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const scopeNote = filters.year && filters.month ? `in ${months[filters.month - 1]} ${filters.year}`
    : filters.year ? `across all of ${filters.year}` : "across all years";
  const meta = browseRoot ? `${yearTiles.length} years${entries.length ? ` + ${entries.length} folders` : ""}`
    : browseYear ? `${monthTiles.length} months${entries.length ? ` + ${entries.length} folders` : ""}`
    : `${entries.length}${entries.length === 200 ? "+" : ""} results`;

  return (
    <div className="page-stack">
      <section className="search-panel">
        <div className="search-box"><span>⌕</span><input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search Case ID, animal, species, location or person…" /></div>
        <div className="filter-bar">
          <select aria-label="Type" value={filters.kind} onChange={(event) => setFilters({ ...filters, kind: event.target.value as ArchiveFilters["kind"] })}>
            <option value="all">All types</option><option value="case">Cases</option><option value="engagement">Engagements</option><option value="other">Other folders</option>
          </select>
          <select aria-label="Year" value={filters.year ?? ""} onChange={(event) => { const y = event.target.value ? Number(event.target.value) : null; setFilters({ ...filters, year: y, month: null }); }}>
            <option value="">Any year</option>{years.map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
          <select aria-label="Month" value={filters.month ?? ""} disabled={!filters.year} onChange={(event) => setFilters({ ...filters, month: event.target.value ? Number(event.target.value) : null })}>
            <option value="">Any month</option>{months.map((month, index) => <option key={month} value={index + 1}>{month}</option>)}
          </select>
          <select aria-label="Species" value={filters.species} onChange={(event) => setFilters({ ...filters, species: event.target.value })}>
            <option value="">Any species</option>{species.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>
        <div className="search-meta"><span>{meta}</span><span>{scopeNote}</span></div>
      </section>
      <section className="entry-grid">
        {browseRoot && yearTiles.map((y) => (
          <button key={`y${y.year}`} className="browse-tile" onClick={() => setFilters({ ...filters, year: y.year, month: null })}>
            <span className="bt-folder">📁</span>
            <div className="bt-body"><div className="bt-title">{y.year}</div><div className="bt-sub">{y.count} items</div></div>
            <span className="bt-chev">›</span>
          </button>
        ))}
        {browseYear && monthTiles.map((m) => (
          <button key={`m${m.month}`} className="browse-tile" onClick={() => setFilters({ ...filters, month: m.month })}>
            <span className="bt-folder">📁</span>
            <div className="bt-body"><div className="bt-title">{months[m.month - 1]} {filters.year}</div><div className="bt-sub">{m.count} folders</div></div>
            <span className="bt-chev">›</span>
          </button>
        ))}
        {entries.map((entry) => <EntryCard key={entry.id} entry={entry} canManage={canManage} onFollowUp={onFollowUp} />)}
        {!browseRoot && !browseYear && entries.length === 0 && <Empty title="No archive results" text="Try another Case ID, animal or location." />}
      </section>
    </div>
  );
}

function FollowUpsView({ items, canManage, onEdit, onComplete }: { items: FollowUp[]; canManage: boolean; onEdit: (item: FollowUp) => void; onComplete: (item: FollowUp) => Promise<void> }) {
  return (
    <div className="page-stack">
      <section className="followup-intro"><div><h2>Active collection requests</h2><p>Ordered by due date so the most urgent work stays visible.</p></div><span>{items.length} active</span></section>
      <section className="panel followup-list">
        {items.length === 0 ? <Empty title="The queue is clear" text="Start a follow-up from any case in Archive." /> : items.map((item) => (
          <div className="followup-detail" key={item.id}>
            <div className={`priority-bar ${item.priority}`} />
            <div className="followup-main"><div className="followup-title"><span className={`priority-pill ${item.priority}`}>{item.priority}</span><h3>{item.entry?.case_id ?? item.entry?.name ?? "Case"}</h3></div><p className="entry-name">{item.entry?.case_id ? item.entry.name : ""}</p><p className="instructions">{item.instructions}</p><div className="followup-meta"><span>Assigned: <strong>{assigneeName(item)}</strong></span><span>Due: <strong>{formatDate(item.due_at)}</strong></span><span>Last media: <strong>{relativeDays(item.entry?.last_media_at ?? null)}</strong></span></div></div>
            <div className="followup-actions">{item.entry?.drive_locations?.map((loc, i) => (<a key={loc.id} className="button secondary" href={loc.web_view_link} target="_blank" rel="noreferrer" title={loc.folder_name}>Open Drive{(item.entry?.drive_locations?.length ?? 0) > 1 ? ` ${i + 1}` : ""}</a>))}{canManage && <button className="button secondary" onClick={() => onEdit(item)}>Edit</button>}{canManage && <button className="button primary" onClick={() => void onComplete(item)}>Mark complete</button>}</div>
          </div>
        ))}
      </section>
    </div>
  );
}

function FollowUpRow({ item }: { item: FollowUp }) {
  return <div className="queue-row"><span className={`priority-dot ${item.priority}`} /><div><strong>{item.entry?.case_id ?? item.entry?.name ?? "Case"}</strong><small>{item.entry?.case_id ? item.entry.name : item.instructions}</small></div><span>{assigneeName(item)}</span><span>{formatDate(item.due_at)}</span></div>;
}

function assigneeName(item: FollowUp): string {
  return teamMemberName(item.assigned_person) || item.assignee?.full_name || item.assignee?.email || "All media team";
}

function Empty({ title, text }: { title: string; text: string }) {
  return <div className="empty-state"><span>✦</span><h3>{title}</h3><p>{text}</p></div>;
}
